import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, TrashIcon, ArrowLeftIcon, ArrowRightIcon, CheckIcon } from '@heroicons/react/24/outline';
import { createRfq } from '../api/marketplaceApi';

interface RfqItemForm {
  title: string;
  catalog_product_id?: number;
  commercial_unit: string;
  commercial_quantity: number;
  grade?: string;
  max_price?: number;
  specifications?: string;
}

const STEPS = ['Requirements', 'Delivery', 'Audience', 'Terms', 'Review'];

const RfqBuilder: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    title: '',
    description: '',
    visibility: 'qualified_suppliers' as string,
    currency: 'RWF',
    quote_deadline: '',
    delivery_destination: '',
    delivery_schedule: '',
    split_delivery_allowed: false,
    payment_terms: '',
  });

  const [items, setItems] = useState<RfqItemForm[]>([
    { title: '', commercial_unit: 'kg', commercial_quantity: 1 },
  ]);

  const addItem = () => setItems([...items, { title: '', commercial_unit: 'kg', commercial_quantity: 1 }]);
  const removeItem = (idx: number) => setItems(items.filter((_, i) => i !== idx));
  const updateItem = (idx: number, field: keyof RfqItemForm, value: any) => {
    const newItems = [...items];
    (newItems[idx] as any)[field] = value;
    setItems(newItems);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const validItems = items.filter(i => i.title && i.commercial_quantity > 0);
      await createRfq({ ...form, items: validItems });
      navigate('/marketplace/buyer/rfqs');
    } catch (err: any) {
      alert(err.message || 'Failed to create RFQ');
    } finally {
      setSubmitting(false);
    }
  };

  const isValid = () => {
    if (step === 0) return form.title && items.some(i => i.title && i.commercial_quantity > 0);
    return true;
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeftIcon className="w-4 h-4" /> Back
      </button>

      <h2 className="text-xl font-bold text-gray-900">Create Request for Quotation</h2>

      {/* Step indicator */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-1.5 text-xs font-medium ${i <= step ? 'text-emerald-600' : 'text-gray-400'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                i < step ? 'bg-emerald-600 text-white' : i === step ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'
              }`}>
                {i < step ? <CheckIcon className="w-3 h-3" /> : i + 1}
              </div>
              <span className="hidden md:inline">{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-gray-200" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        {step === 0 && (
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-900">Requirements</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">RFQ Title *</label>
              <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="e.g., Coffee beans for Q1 2026" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" rows={3} placeholder="Additional details..." />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Products *</label>
              {items.map((item, idx) => (
                <div key={idx} className="flex gap-2 mb-2 items-start">
                  <input type="text" value={item.title} onChange={e => updateItem(idx, 'title', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Product name" />
                  <select value={item.commercial_unit} onChange={e => updateItem(idx, 'commercial_unit', e.target.value)}
                    className="px-2 py-2 border border-gray-200 rounded-lg text-sm">
                    <option value="kg">kg</option><option value="tonne">tonne</option><option value="box">box</option>
                    <option value="crate">crate</option><option value="bag">bag</option><option value="piece">piece</option>
                    <option value="litre">litre</option>
                  </select>
                  <input type="number" value={item.commercial_quantity} min={0.01} step={0.01}
                    onChange={e => updateItem(idx, 'commercial_quantity', parseFloat(e.target.value) || 0)}
                    className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Qty" />
                  {items.length > 1 && (
                    <button onClick={() => removeItem(idx)} className="p-2 text-red-400 hover:text-red-600">
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button onClick={addItem} className="flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-700">
                <PlusIcon className="w-4 h-4" /> Add product
              </button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-900">Delivery</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Destination</label>
              <input type="text" value={form.delivery_destination} onChange={e => setForm({ ...form, delivery_destination: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Delivery address" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Schedule</label>
              <input type="text" value={form.delivery_schedule} onChange={e => setForm({ ...form, delivery_schedule: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="e.g., Weekly deliveries for 3 months" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.split_delivery_allowed}
                onChange={e => setForm({ ...form, split_delivery_allowed: e.target.checked })}
                className="rounded border-gray-300" />
              <label className="text-sm text-gray-700">Allow split deliveries</label>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-900">Supplier Audience</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Visibility</label>
              <div className="space-y-2">
                {[
                  { value: 'qualified_suppliers', label: 'Qualified Suppliers', desc: 'Invite verified suppliers matching your requirements' },
                  { value: 'selected_suppliers', label: 'Selected Suppliers', desc: 'Invite specific suppliers you choose' },
                  { value: 'public_marketplace', label: 'Public Marketplace', desc: 'Open to all verified suppliers (subject to platform policy)' },
                ].map(opt => (
                  <label key={opt.value} className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer ${
                    form.visibility === opt.value ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input type="radio" name="visibility" value={opt.value} checked={form.visibility === opt.value}
                      onChange={e => setForm({ ...form, visibility: e.target.value })}
                      className="mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{opt.label}</p>
                      <p className="text-xs text-gray-500">{opt.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-900">Commercial Terms</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                  <option value="RWF">RWF</option><option value="USD">USD</option><option value="KES">KES</option>
                  <option value="UGX">UGX</option><option value="TZS">TZS</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quote Deadline</label>
                <input type="datetime-local" value={form.quote_deadline} onChange={e => setForm({ ...form, quote_deadline: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
              <textarea value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" rows={2} placeholder="Payment terms..." />
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-900">Review & Publish</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              <p><span className="font-medium">Title:</span> {form.title}</p>
              <p><span className="font-medium">Items:</span> {items.filter(i => i.title).map(i => `${i.title} (${i.commercial_quantity} ${i.commercial_unit})`).join(', ')}</p>
              <p><span className="font-medium">Delivery:</span> {form.delivery_destination || 'Not specified'}</p>
              <p><span className="font-medium">Visibility:</span> {form.visibility.replace('_', ' ')}</p>
              <p><span className="font-medium">Currency:</span> {form.currency}</p>
              {form.quote_deadline && <p><span className="font-medium">Deadline:</span> {new Date(form.quote_deadline).toLocaleString()}</p>}
              <p><span className="font-medium">Split delivery:</span> {form.split_delivery_allowed ? 'Yes' : 'No'}</p>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
          className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40">
          <ArrowLeftIcon className="w-4 h-4" /> Previous
        </button>
        {step < STEPS.length - 1 ? (
          <button onClick={() => setStep(step + 1)} disabled={!isValid()}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-40">
            Next <ArrowRightIcon className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={handleSubmit} disabled={submitting || !isValid()}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-40">
            {submitting ? 'Publishing...' : 'Publish RFQ'}
          </button>
        )}
      </div>
    </div>
  );
};

export default RfqBuilder;
